type RmdfRawMaterialSource = {
  tcProductCategoryCode?: string;
  tcProductDetailCode?: string;
  rawMaterialCode?: string;
  rawMaterialPercentage?: number;
  standard?: string;
  isCertified?: string;
  tcProductNetShippingWeight?: number;
  tcProductSupplementaryWeight?: number;
  tcProductAdditivesWeight?: number;
};

export const INCOMING_TC_STEP_LABELS = [
  "Incoming TC Information",
  "Incoming TC Product Information",
  "Incoming TC Net Weight Used Information",
] as const;

export const getIncomingTcSourceVersion = (
  incomingV4IssuedFlag: unknown
): "V3" | "V4" =>
  String(incomingV4IssuedFlag || "").trim().toUpperCase() === "Y" ? "V4" : "V3";

const extractLeadingCode = (value: any): string => {
  const text = String(value || "").trim();
  if (!text) return "";
  const directMatch = text.match(/\b(?:PC|PD|RM)\d{4}\b/i);
  return directMatch ? directMatch[0].toUpperCase() : text.split(" - ")[0].trim();
};

const extractWeight = (value: any): number => {
  const weight = Number(value);
  return Number.isFinite(weight) ? Math.abs(weight) : 0;
};

export const filterIncomingCertificateTypes = <T extends { value: string }>(
  options: T[],
  incomingV4IssuedFlag: unknown
): T[] =>
  String(incomingV4IssuedFlag || "").trim().toUpperCase() === "Y"
    ? options.filter((option) => option.value === "TC")
    : options;

export const ensureExternalIncomingCertificateTypes = <
  T extends { value: string; label: string }
>(options: T[]): Array<T | { value: string; label: string }> => {
  const normalizedOptions = options.map((option) =>
    String(option.value || "").trim().toUpperCase() === "FPF"
      ? { ...option, label: "FPF" }
      : option
  );
  const availableValues = new Set(
    normalizedOptions.map((option) =>
      String(option.value || "").trim().toUpperCase()
    )
  );
  const fallbackOptions = [
    { value: "RMDF", label: "RMDF" },
    { value: "FPF", label: "FPF" },
  ].filter((option) => !availableValues.has(option.value));

  return [...normalizedOptions, ...fallbackOptions];
};

export const unwrapRmdfLookupItems = (payload: any): any[] => {
  let items = payload?.response ?? payload ?? [];
  if (typeof items === "string") {
    try {
      items = JSON.parse(items);
    } catch {
      return [];
    }
  }
  return Array.isArray(items) ? items : [];
};

export const extractRmdfPercentage = (item: any, rawMaterialCode?: string): number => {
  const direct = Number(
    item?.rawMaterialPercentage ??
    item?.rmPercentage ??
    item?.certifiedMaterialComposition ??
    item?.certifiedMaterialPercentage
  );
  if (Number.isFinite(direct) && direct > 0) return direct;

  const composition = String(item?.rmComposition || "");
  const normalizedCode = extractLeadingCode(rawMaterialCode).toUpperCase();
  if (normalizedCode) {
    const escapedCode = normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const codeMatch = composition.match(
      new RegExp(`\\b${escapedCode}\\s*[-:]\\s*(\\d+(?:\\.\\d+)?)\\b`, "i")
    );
    const codePercentage = Number(codeMatch?.[1]);
    if (Number.isFinite(codePercentage) && codePercentage > 0) return codePercentage;
  }

  // Do not apply the final suffix of a multi-RM composition to every row.
  const compositionMatches = Array.from(
    composition.matchAll(/\bRM\d{4}\s*[-:]\s*(\d+(?:\.\d+)?)\b/gi)
  );
  if (compositionMatches.length === 1) {
    const percentage = Number(compositionMatches[0][1]);
    if (Number.isFinite(percentage) && percentage > 0) return percentage;
  }
  return 100;
};

export const normalizeRmdfCertifiedFlag = (item: any): "Y" | "N" => {
  const explicitFlag = String(item?.isCertified ?? "").trim().toUpperCase();
  if (["Y", "YES", "TRUE"].includes(explicitFlag)) return "Y";
  if (["N", "NO", "FALSE"].includes(explicitFlag)) return "N";
  if (typeof item?.isSellerScopeCode === "boolean") {
    return item.isSellerScopeCode ? "Y" : "N";
  }
  return "Y";
};

export const buildRmdfArticleMaps = (rawMaterials: any[]) => {
  const standardsByArticle: { [productNo: string]: string[] } = {};
  const rawMaterialsByArticle: { [productNo: string]: RmdfRawMaterialSource[] } = {};
  const maxWeightByArticle: { [productNo: string]: number } = {};
  const sourceByArticle: { [productNo: string]: RmdfRawMaterialSource } = {};

  rawMaterials.forEach((rm: any) => {
    const articleNo = String(rm?.productNumber || "").trim();
    if (!articleNo) return;

    standardsByArticle[articleNo] = ["RMDF"];
    const availableWeight = extractWeight(
      rm?.balance || rm?.certifiedWeightBought || rm?.totalReclaimedWeight
    );
    const rawMaterialCode = extractLeadingCode(
      rm?.rmCode || rm?.certifiedRmCode || rm?.rawMaterialCode
    );
    const sourceRow: RmdfRawMaterialSource = {
      tcProductCategoryCode: extractLeadingCode(rm?.productCategory),
      tcProductDetailCode: extractLeadingCode(rm?.productDetail),
      rawMaterialCode,
      rawMaterialPercentage: extractRmdfPercentage(rm, rawMaterialCode),
      standard: "RMDF",
      isCertified: normalizeRmdfCertifiedFlag(rm),
      tcProductNetShippingWeight: availableWeight,
      tcProductSupplementaryWeight: 0,
      tcProductAdditivesWeight: 0,
    };
    rawMaterialsByArticle[articleNo] = rawMaterialsByArticle[articleNo] || [];
    const duplicate = rawMaterialsByArticle[articleNo].some(
      (item) =>
        item.tcProductCategoryCode === sourceRow.tcProductCategoryCode &&
        item.tcProductDetailCode === sourceRow.tcProductDetailCode &&
        item.rawMaterialCode === sourceRow.rawMaterialCode &&
        item.rawMaterialPercentage === sourceRow.rawMaterialPercentage
    );
    if (!duplicate) rawMaterialsByArticle[articleNo].push(sourceRow);
    sourceByArticle[articleNo] = sourceByArticle[articleNo] || sourceRow;

    if (availableWeight > 0) {
      maxWeightByArticle[articleNo] = (maxWeightByArticle[articleNo] || 0) + availableWeight;
    }
  });

  return {
    standards: ["RMDF"],
    standardsByArticle,
    sourceByArticle,
    rawMaterialsByArticle,
    maxWeightByArticle,
  };
};
